import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Download,
  Copy,
  Trash2,
  BedDouble,
  ZoomIn,
  ZoomOut,
  Square,
  Link2,
  Table2,
  KeyRound,
  ShieldCheck,
  Columns3,
  Maximize2,
  Minimize2,
  GripVertical,
} from "lucide-react";

/* ----------------------------- TechApp UI ----------------------------- */

const AppBackground = ({ children }) => (
  <div className="min-h-screen w-full bg-[#07090c] text-white relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-[80px]" />
      <div className="absolute -bottom-28 -right-24 h-[560px] w-[560px] rounded-full bg-sky-400/10 blur-[90px]" />
      <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
      <div className="absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/70" />
    </div>
    <div className="relative">{children}</div>
  </div>
);

const Glass = React.forwardRef(({ children, className = "" }, ref) => (
  <div
    ref={ref}
    className={[
      "rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl",
      "shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]",
      className,
    ].join(" ")}
  >
    {children}
  </div>
));

const PrimaryButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "px-4 py-2.5 rounded-2xl font-semibold text-sm",
      "bg-white text-black hover:bg-white/90",
      "shadow-[0_12px_30px_-18px_rgba(255,255,255,0.50)]",
      "transition disabled:opacity-50 disabled:hover:bg-white",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const GhostButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "px-4 py-2.5 rounded-2xl font-semibold text-sm",
      "border border-white/12 bg-white/5 hover:bg-white/8",
      "text-white/85 transition disabled:opacity-50",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const IconButton = ({ title, onClick, disabled, children, className = "" }) => (
  <button
    title={title}
    onClick={onClick}
    disabled={disabled}
    className={[
      "h-10 w-10 rounded-2xl border border-white/12 bg-white/5 hover:bg-white/8",
      "flex items-center justify-center text-white/85 transition",
      "disabled:opacity-50",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const Pill = ({ children, tone = "neutral", className = "" }) => {
  const cls =
    tone === "emerald"
      ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-100"
      : tone === "sky"
      ? "border-sky-400/20 bg-sky-400/10 text-sky-100"
      : tone === "danger"
      ? "border-red-400/20 bg-red-400/10 text-red-100"
      : "border-white/12 bg-white/6 text-white/80";

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide",
        cls,
        className,
      ].join(" ")}
    >
      {children}
    </span>
  );
};

/* ----------------------------- Helpers ----------------------------- */

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id_${Math.random().toString(16).slice(2)}_${Date.now()}`);

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const snap = (n, grid) => Math.round(n / grid) * grid;

const toBoardPoint = (clientX, clientY, boardRect, viewport) => {
  const x = (clientX - boardRect.left - viewport.x) / viewport.scale;
  const y = (clientY - boardRect.top - viewport.y) / viewport.scale;
  return { x, y };
};

const DEFAULT_TABLE_SIZE = { w: 360, h: 280 };
const GRID = 20;

const TYPE_OPTIONS = [
  "uuid",
  "int",
  "bigint",
  "varchar(255)",
  "text",
  "boolean",
  "timestamp",
  "date",
  "json",
  "float",
  "decimal(10,2)",
];

/* ----------------------------- Component ----------------------------- */

export default function DatabaseWhiteboard() {
  const boardRef = useRef(null);
  const boardWrapRef = useRef(null);

  const [viewport, setViewport] = useState({ x: 120, y: 90, scale: 1 });
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // tables
  const [tables, setTables] = useState(() => {
    const usersId = uid();
    const postsId = uid();

    const userColId = uid();
    const postId = uid();
    const postUserId = uid();

    return [
      {
        id: usersId,
        kind: "table",
        name: "users",
        position: { x: 120, y: 140 },
        size: { ...DEFAULT_TABLE_SIZE },
        columns: [
          { id: userColId, name: "id", type: "uuid", pk: true, unique: true, nullable: false },
          { id: uid(), name: "email", type: "varchar(255)", pk: false, unique: true, nullable: false },
          { id: uid(), name: "created_at", type: "timestamp", pk: false, unique: false, nullable: false },
        ],
      },
      {
        id: postsId,
        kind: "table",
        name: "posts",
        position: { x: 590, y: 170 },
        size: { ...DEFAULT_TABLE_SIZE },
        columns: [
          { id: postId, name: "id", type: "uuid", pk: true, unique: true, nullable: false },
          { id: postUserId, name: "user_id", type: "uuid", pk: false, unique: false, nullable: false },
          { id: uid(), name: "title", type: "varchar(255)", pk: false, unique: false, nullable: false },
          { id: uid(), name: "body", type: "text", pk: false, unique: false, nullable: true },
          { id: uid(), name: "created_at", type: "timestamp", pk: false, unique: false, nullable: false },
        ],
      },
    ];
  });

  // relationships (FK edges)
  const [rels, setRels] = useState(() => []);

  // selection
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedColumnId, setSelectedColumnId] = useState(null);

  const selectedTable = useMemo(
    () => tables.find((t) => t.id === selectedTableId) || null,
    [tables, selectedTableId]
  );

  const selectedColumn = useMemo(() => {
    if (!selectedTable || !selectedColumnId) return null;
    return selectedTable.columns.find((c) => c.id === selectedColumnId) || null;
  }, [selectedTable, selectedColumnId]);

  // interaction
  const [mode, setMode] = useState("select"); // select | pan | connect
  const dragRef = useRef(null); // {type:'table'|'pan', ...}
  const linkRef = useRef(null); // { fromTableId, fromColumnId, pointerId }
  const [linkPreview, setLinkPreview] = useState(null); // {from, to}
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 1600);
    return () => clearTimeout(t);
  }, [toast]);

  const boardRect = () => boardRef.current?.getBoundingClientRect?.() || null;

  /* ----------------------------- Fullscreen ----------------------------- */

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  /* ----------------------------- Add Table ----------------------------- */

  const addTable = () => {
    const id = uid();
    const rect = boardRect();

    let pos = { x: 240, y: 200 };
    if (rect) {
      const cx = rect.left + rect.width * 0.52;
      const cy = rect.top + rect.height * 0.45;
      pos = toBoardPoint(cx, cy, rect, viewport);
    }

    const t = {
      id,
      kind: "table",
      name: `table_${tables.length + 1}`,
      position: {
        x: snapToGrid ? snap(pos.x, GRID) : pos.x,
        y: snapToGrid ? snap(pos.y, GRID) : pos.y,
      },
      size: { ...DEFAULT_TABLE_SIZE },
      columns: [
        { id: uid(), name: "id", type: "uuid", pk: true, unique: true, nullable: false },
        { id: uid(), name: "created_at", type: "timestamp", pk: false, unique: false, nullable: false },
      ],
    };

    setTables((prev) => [...prev, t]);
    setSelectedTableId(id);
    setSelectedColumnId(null);
  };

  /* ----------------------------- Table ops ----------------------------- */

  const deleteSelected = () => {
    if (!selectedTableId) return;
    const tid = selectedTableId;
    setTables((prev) => prev.filter((t) => t.id !== tid));
    setRels((prev) => prev.filter((r) => r.fromTableId !== tid && r.toTableId !== tid));
    setSelectedTableId(null);
    setSelectedColumnId(null);
  };

  const duplicateSelected = () => {
    if (!selectedTable) return;
    const newId = uid();

    const cols = selectedTable.columns.map((c) => ({ ...c, id: uid() }));
    const copy = {
      ...selectedTable,
      id: newId,
      name: `${selectedTable.name}_copy`,
      position: { x: selectedTable.position.x + 32, y: selectedTable.position.y + 32 },
      columns: cols,
    };

    setTables((prev) => [...prev, copy]);
    setSelectedTableId(newId);
    setSelectedColumnId(null);
  };

  const updateSelectedTable = (patch) => {
    if (!selectedTableId) return;
    setTables((prev) => prev.map((t) => (t.id === selectedTableId ? { ...t, ...patch } : t)));
  };

  const updateSelectedColumn = (patch) => {
    if (!selectedTableId || !selectedColumnId) return;
    setTables((prev) =>
      prev.map((t) => {
        if (t.id !== selectedTableId) return t;
        return {
          ...t,
          columns: t.columns.map((c) => (c.id === selectedColumnId ? { ...c, ...patch } : c)),
        };
      })
    );
  };

  const addColumn = () => {
    if (!selectedTableId) return;
    const newCol = {
      id: uid(),
      name: `column_${(selectedTable?.columns?.length || 0) + 1}`,
      type: "varchar(255)",
      pk: false,
      unique: false,
      nullable: true,
    };
    setTables((prev) =>
      prev.map((t) => (t.id === selectedTableId ? { ...t, columns: [...t.columns, newCol] } : t))
    );
    setSelectedColumnId(newCol.id);
  };

  const deleteColumn = () => {
    if (!selectedTableId || !selectedColumnId) return;
    const tid = selectedTableId;
    const cid = selectedColumnId;

    setTables((prev) =>
      prev.map((t) =>
        t.id === tid ? { ...t, columns: t.columns.filter((c) => c.id !== cid) } : t
      )
    );

    setRels((prev) =>
      prev.filter(
        (r) =>
          !(r.fromTableId === tid && r.fromColumnId === cid) &&
          !(r.toTableId === tid && r.toColumnId === cid)
      )
    );

    setSelectedColumnId(null);
  };

  /* ----------------------------- Keyboard ----------------------------- */

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const typing = tag === "input" || tag === "textarea" || tag === "select";

      if (!typing && (e.key === "Delete" || e.key === "Backspace")) deleteSelected();

      if (!typing && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") {
        e.preventDefault();
        duplicateSelected();
      }

      if (e.key === " " && mode !== "connect") setMode("pan");

      if (e.key === "Escape") {
        linkRef.current = null;
        setLinkPreview(null);
        setMode("select");
      }
    };

    const onKeyUp = (e) => {
      if (e.key === " " && mode === "pan") setMode("select");
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedTableId, selectedTable]);

  /* ----------------------------- Zoom/Pan ----------------------------- */

  const zoomTo = (nextScale, originClient) => {
    const rect = boardRect();
    if (!rect) return;

    const scale = clamp(nextScale, 0.35, 2.25);
    const ox = originClient?.x ?? rect.left + rect.width / 2;
    const oy = originClient?.y ?? rect.top + rect.height / 2;

    const before = toBoardPoint(ox, oy, rect, viewport);
    const next = { ...viewport, scale };

    const afterScreenX = before.x * scale + next.x;
    const afterScreenY = before.y * scale + next.y;

    next.x += ox - rect.left - afterScreenX;
    next.y += oy - rect.top - afterScreenY;

    setViewport(next);
  };

  const onWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.07 : 0.93;
    zoomTo(viewport.scale * factor, { x: e.clientX, y: e.clientY });
  };

  /* ----------------------------- Board Pointer ----------------------------- */

  const onBoardPointerDown = (e) => {
    if (e.button !== 0) return;

    // click empty while connecting cancels
    if (mode === "connect") {
      linkRef.current = null;
      setLinkPreview(null);
      setMode("select");
      return;
    }

    // deselect if click on empty board
    if (e.target === boardRef.current) {
      setSelectedTableId(null);
      setSelectedColumnId(null);
    }

    if (mode === "pan") {
      dragRef.current = {
        type: "pan",
        startClient: { x: e.clientX, y: e.clientY },
        startViewport: { ...viewport },
        pointerId: e.pointerId,
      };
      boardRef.current?.setPointerCapture?.(e.pointerId);
    }
  };

  const onBoardPointerMove = (e) => {
    const rect = boardRect();
    if (!rect) return;

    // pan drag
    if (dragRef.current?.type === "pan") {
      const dx = e.clientX - dragRef.current.startClient.x;
      const dy = e.clientY - dragRef.current.startClient.y;
      setViewport({
        ...dragRef.current.startViewport,
        x: dragRef.current.startViewport.x + dx,
        y: dragRef.current.startViewport.y + dy,
      });
      return;
    }

    // table drag
    if (dragRef.current?.type === "table") {
      const p = toBoardPoint(e.clientX, e.clientY, rect, viewport);
      let nx = p.x - dragRef.current.grabOffset.x;
      let ny = p.y - dragRef.current.grabOffset.y;
      if (snapToGrid) {
        nx = snap(nx, GRID);
        ny = snap(ny, GRID);
      }
      const tid = dragRef.current.tableId;
      setTables((prev) =>
        prev.map((t) => (t.id === tid ? { ...t, position: { x: nx, y: ny } } : t))
      );
      return;
    }

    // linking preview (smooth and consistent)
    if (linkRef.current) {
      const to = toBoardPoint(e.clientX, e.clientY, rect, viewport);
      const fromAnchor = getColumnAnchor(tables, linkRef.current.fromTableId, linkRef.current.fromColumnId, "out");
      if (!fromAnchor) return;
      setLinkPreview({ from: fromAnchor, to });
    }
  };

  // Detect if pointer lands on an IN handle dot using elementsFromPoint
  const getInHandleTarget = (clientX, clientY) => {
    const elements = document.elementsFromPoint(clientX, clientY);
    for (const el of elements) {
      const tid = el.dataset?.tableId;
      const cid = el.dataset?.columnId;
      if (tid && cid) return { toTableId: tid, toColumnId: cid };
    }
    return null;
  };

  const completeConnect = (e, toTableId, toColumnId) => {
    e.stopPropagation();
    if (!linkRef.current) return;

    const { fromTableId, fromColumnId, pointerId } = linkRef.current;

    // prevent self connect same column
    if (fromTableId === toTableId && fromColumnId === toColumnId) {
      setToast("Can't connect column to itself");
      try { boardRef.current?.releasePointerCapture?.(pointerId); } catch {}
      linkRef.current = null;
      setLinkPreview(null);
      setMode("select");
      return;
    }

    // prevent duplicates
    const exists = rels.some(
      (r) =>
        r.fromTableId === fromTableId &&
        r.fromColumnId === fromColumnId &&
        r.toTableId === toTableId &&
        r.toColumnId === toColumnId
    );
    if (exists) {
      setToast("Relationship already exists");
      try { boardRef.current?.releasePointerCapture?.(pointerId); } catch {}
      linkRef.current = null;
      setLinkPreview(null);
      setMode("select");
      return;
    }

    setRels((prev) => [
      ...prev,
      {
        id: uid(),
        kind: "FK",
        fromTableId,
        fromColumnId,
        toTableId,
        toColumnId,
        cardinality: "N:1",
        onDelete: "CASCADE",
        onUpdate: "CASCADE",
      },
    ]);

    setToast("FK created ✅");

    // clear connect state + release capture
    linkRef.current = null;
    setLinkPreview(null);
    setMode("select");
    try {
      boardRef.current?.releasePointerCapture?.(pointerId);
    } catch {}
  };

    const onBoardPointerUp = (e) => {
    // release pan or table
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
      try { boardRef.current?.releasePointerCapture?.(e.pointerId); } catch {}
    }

    // if linking: check if dropped on an IN handle via hit-test, then complete
    if (linkRef.current?.pointerId === e.pointerId) {
      const target = getInHandleTarget(e.clientX, e.clientY);
      if (target) {
        completeConnect(e, target.toTableId, target.toColumnId);
      } else {
        // dropped on empty area — cancel
        try { boardRef.current?.releasePointerCapture?.(e.pointerId); } catch {}
        linkRef.current = null;
        setLinkPreview(null);
        setMode("select");
      }
    }
  };

  /* ----------------------------- Table Drag ----------------------------- */

  const onTableHeaderPointerDown = (e, tableId) => {
    if (mode === "connect") return;
    if (e.button !== 0) return;

    const rect = boardRect();
    if (!rect) return;

    const t = tables.find((x) => x.id === tableId);
    if (!t) return;

    setSelectedTableId(tableId);
    setSelectedColumnId(null);

    const p = toBoardPoint(e.clientX, e.clientY, rect, viewport);
    dragRef.current = {
      type: "table",
      tableId,
      grabOffset: { x: p.x - t.position.x, y: p.y - t.position.y },
      pointerId: e.pointerId,
    };
    boardRef.current?.setPointerCapture?.(e.pointerId);
  };

  /* ----------------------------- FK Connect ----------------------------- */

  const beginConnect = (e, fromTableId, fromColumnId) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = boardRect();
    if (!rect) return;

    setMode("connect");
    const pointerId = e.pointerId;
    linkRef.current = { fromTableId, fromColumnId, pointerId };

    // capture pointer on the board so move events always fire even outside the dot
    try {
      boardRef.current?.setPointerCapture?.(pointerId);
    } catch {}

    const from = getColumnAnchor(tables, fromTableId, fromColumnId, "out");
    const to = toBoardPoint(e.clientX, e.clientY, rect, viewport);
    if (from) setLinkPreview({ from, to });
  };

    /* ----------------------------- Export ----------------------------- */

  const exportJSON = () => {
    const payload = {
      version: "1.0",
      exportedAt: new Date().toISOString(),
      viewport: { ...viewport },
      settings: { snapToGrid, gridSize: GRID },
      schema: {
        tables: tables.map((t) => ({
          id: t.id,
          name: t.name,
          kind: t.kind,
          position: t.position,
          size: t.size,
          columns: t.columns.map((c) => ({
            id: c.id,
            name: c.name,
            type: c.type,
            pk: !!c.pk,
            unique: !!c.unique,
            nullable: !!c.nullable,
          })),
        })),
        relationships: rels.map((r) => ({
          id: r.id,
          kind: r.kind,
          fromTableId: r.fromTableId,
          fromColumnId: r.fromColumnId,
          toTableId: r.toTableId,
          toColumnId: r.toColumnId,
          cardinality: r.cardinality,
          onDelete: r.onDelete,
          onUpdate: r.onUpdate,
        })),
      },
    };

    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "database-whiteboard.json";
    a.click();
    URL.revokeObjectURL(url);
    setToast("Exported JSON ✅");
  };

  const copyJSON = async () => {
    const payload = {
      version: "1.0",
      exportedAt: new Date().toISOString(),
      viewport: { ...viewport },
      settings: { snapToGrid, gridSize: GRID },
      schema: { tables, relationships: rels },
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setToast("Copied JSON ✅");
    } catch {
      setToast("Clipboard blocked — use Export");
    }
  };

  /* ----------------------------- Render Relationships ----------------------------- */

  const renderedRels = useMemo(() => {
    return rels
      .map((r) => {
        const a = getColumnAnchor(tables, r.fromTableId, r.fromColumnId, "out");
        const b = getColumnAnchor(tables, r.toTableId, r.toColumnId, "in");
        if (!a || !b) return null;
        return { ...r, path: makeBezier(a, b) };
      })
      .filter(Boolean);
  }, [rels, tables]);

  return (
    <AppBackground>
      <div className="mx-auto max-w-[1450px] px-6 py-8 mt-20">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left panel */}
          <Glass className="lg:w-[390px] p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-semibold tracking-tight flex items-center gap-2">
                  <Table2 className="h-5 w-5 text-white/75" />
                  Database Whiteboard
                </div>
                <div className="text-white/60 text-sm mt-1">
                  ERD tables • FK connections • exportable schema
                </div>
              </div>

              <Pill tone={mode === "connect" ? "sky" : "neutral"}>
                <Link2 className="h-3.5 w-3.5" />
                {mode === "connect" ? "Linking" : "Ready"}
              </Pill>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2">
              <PrimaryButton onClick={addTable} className="col-span-1">
                <span className="inline-flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add
                </span>
              </PrimaryButton>
              <GhostButton onClick={copyJSON} className="col-span-1">
                <span className="inline-flex items-center gap-2">
                  <Copy className="h-4 w-4" />
                  Copy
                </span>
              </GhostButton>
              <GhostButton onClick={exportJSON} className="col-span-1">
                <span className="inline-flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export
                </span>
              </GhostButton>
            </div>

            <div className="mt-4 rounded-2xl border border-white/12 bg-white/5 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <Columns3 className="h-4 w-4 text-white/70" />
                  Controls
                </div>
                <Pill tone="emerald">
                  <span className="text-[11px]">Space</span>=Pan
                </Pill>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <IconButton title="Zoom In" onClick={() => zoomTo(viewport.scale * 1.12)}>
                  <ZoomIn className="h-4 w-4" />
                </IconButton>
                <IconButton title="Zoom Out" onClick={() => zoomTo(viewport.scale * 0.9)}>
                  <ZoomOut className="h-4 w-4" />
                </IconButton>
                <IconButton title="Reset View" onClick={() => setViewport({ x: 120, y: 90, scale: 1 })}>
                  <Square className="h-4 w-4" />
                </IconButton>

                <div className="ml-auto flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm text-white/70 select-none">
                    <input
                      type="checkbox"
                      checked={snapToGrid}
                      onChange={(e) => setSnapToGrid(e.target.checked)}
                      className="accent-white"
                    />
                    Snap
                  </label>
                </div>
              </div>

              <div className="mt-3 text-[12px] text-white/55 leading-relaxed">
                FK link: drag from <span className="text-emerald-200">right dot</span> → drop on{" "}
                <span className="text-sky-200">left dot</span>.
              </div>
            </div>

            {/* Inspector */}
            <div className="mt-5 rounded-2xl border border-white/12 bg-white/5 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-white/70" />
                  Inspector
                </div>
                <div className="text-[12px] text-white/60">{selectedTable ? "Selected" : "None"}</div>
              </div>

              {!selectedTable ? (
                <div className="mt-3 text-sm text-white/60">Click a table or column to edit.</div>
              ) : (
                <div className="mt-3 space-y-4">
                  <div>
                    <div className="text-[12px] text-white/60 mb-1">Table name</div>
                    <input
                      value={selectedTable.name}
                      onChange={(e) => updateSelectedTable({ name: e.target.value })}
                      className="w-full rounded-2xl border border-white/12 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <GhostButton onClick={duplicateSelected} className="w-full">
                      <span className="inline-flex items-center gap-2">
                        <BedDouble className="h-4 w-4" />
                        Duplicate
                      </span>
                    </GhostButton>
                    <GhostButton onClick={deleteSelected} className="w-full border-red-500/20">
                      <span className="inline-flex items-center gap-2 text-red-200">
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </span>
                    </GhostButton>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-black/25 p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-white/70" />
                        Columns
                      </div>
                      <GhostButton onClick={addColumn} className="px-3 py-2">
                        <span className="inline-flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          Add
                        </span>
                      </GhostButton>
                    </div>

                    {selectedColumn ? (
                      <div className="mt-3 space-y-3">
                        <div className="text-[12px] text-white/55">
                          Editing:{" "}
                          <span className="text-white/85 font-semibold">{selectedColumn.name}</span>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <div className="text-[12px] text-white/60 mb-1">Name</div>
                            <input
                              value={selectedColumn.name}
                              onChange={(e) => updateSelectedColumn({ name: e.target.value })}
                              className="w-full rounded-2xl border border-white/12 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
                            />
                          </div>

                          <div>
                            <div className="text-[12px] text-white/60 mb-1">Type</div>
                            <select
                              value={selectedColumn.type}
                              onChange={(e) => updateSelectedColumn({ type: e.target.value })}
                              className="w-full rounded-2xl border border-white/12 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
                            >
                              {TYPE_OPTIONS.map((t) => (
                                <option key={t} value={t} className="bg-black">
                                  {t}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-white/70">
                          <label className="flex items-center gap-2 select-none">
                            <input
                              type="checkbox"
                              checked={!!selectedColumn.pk}
                              onChange={(e) => updateSelectedColumn({ pk: e.target.checked })}
                              className="accent-white"
                            />
                            PK
                          </label>

                          <label className="flex items-center gap-2 select-none">
                            <input
                              type="checkbox"
                              checked={!!selectedColumn.unique}
                              onChange={(e) => updateSelectedColumn({ unique: e.target.checked })}
                              className="accent-white"
                            />
                            UNIQUE
                          </label>

                          <label className="flex items-center gap-2 select-none">
                            <input
                              type="checkbox"
                              checked={!!selectedColumn.nullable}
                              onChange={(e) => updateSelectedColumn({ nullable: e.target.checked })}
                              className="accent-white"
                            />
                            NULL
                          </label>
                        </div>

                        <GhostButton onClick={deleteColumn} className="w-full border-red-500/20">
                          <span className="inline-flex items-center gap-2 text-red-200">
                            <Trash2 className="h-4 w-4" />
                            Delete Column
                          </span>
                        </GhostButton>
                      </div>
                    ) : (
                      <div className="mt-3 text-sm text-white/60">Select a column to edit.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Glass>

          {/* Board */}
          <Glass
            ref={boardWrapRef}
            className={
              isFullscreen
                ? "fixed inset-0 z-50 rounded-none p-4 flex flex-col"
                : "flex-1 p-4"
            }
          >
            {/* top toolbar */}
            <div className="flex items-center justify-between gap-3 px-2 pb-3">
              <div className="flex items-center gap-2">
                <Pill tone="emerald">
                  <Table2 className="h-3.5 w-3.5" />
                  Tables: {tables.length}
                </Pill>
                <Pill tone="sky">
                  <Link2 className="h-3.5 w-3.5" />
                  FKs: {rels.length}
                </Pill>
                <Pill tone="neutral">Zoom: {(viewport.scale * 100).toFixed(0)}%</Pill>
              </div>

              <div className="flex items-center gap-2">
                <GhostButton onClick={() => setMode("select")} className={mode === "select" ? "bg-white/10" : ""}>
                  Select
                </GhostButton>
                <GhostButton onClick={() => setMode("pan")} className={mode === "pan" ? "bg-white/10" : ""}>
                  Pan
                </GhostButton>
                <IconButton
                  title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                  onClick={toggleFullscreen}
                  className="ml-1"
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </IconButton>
              </div>
            </div>

            <div
              ref={boardRef}
              onWheel={onWheel}
              onPointerDown={onBoardPointerDown}
              onPointerMove={onBoardPointerMove}
              onPointerUp={onBoardPointerUp}
              className={[
                "relative w-full overflow-hidden rounded-3xl border border-white/10 bg-black/35",
                isFullscreen ? "flex-1" : "h-[780px]",
              ].join(" ")}
              style={{ touchAction: "none" }}
            >
              {/* grid */}
              <div
                className="pointer-events-none absolute inset-0 opacity-70"
                style={{
                  backgroundImage: `
                    radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px),
                    linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)
                  `,
                  backgroundSize: `26px 26px, 92px 92px`,
                }}
              />

              {/* transform */}
              <div
                className="absolute inset-0"
                style={{
                  transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`,
                  transformOrigin: "0 0",
                }}
              >
                {/* edges */}
                <svg className="absolute inset-0 overflow-visible pointer-events-none">
                  <defs>
                    <linearGradient id="edgeGlow" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="rgba(16,185,129,0.68)" />
                      <stop offset="100%" stopColor="rgba(56,189,248,0.68)" />
                    </linearGradient>
                    <filter id="softGlow">
                      <feGaussianBlur stdDeviation="2.35" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {renderedRels.map((r) => (
                    <path
                      key={r.id}
                      d={r.path}
                      fill="none"
                      stroke="url(#edgeGlow)"
                      strokeWidth="3"
                      opacity="0.95"
                      filter="url(#softGlow)"
                    />
                  ))}

                  {linkPreview && (
                    <path
                      d={makeBezier(linkPreview.from, linkPreview.to)}
                      fill="none"
                      stroke="rgba(255,255,255,0.45)"
                      strokeWidth="2.5"
                      strokeDasharray="7 7"
                    />
                  )}
                </svg>

                {/* tables */}
                {tables.map((t) => (
                  <TableNode
                    key={t.id}
                    table={t}
                    selected={t.id === selectedTableId}
                    selectedColumnId={selectedTableId === t.id ? selectedColumnId : null}
                    onSelectTable={() => {
                      setSelectedTableId(t.id);
                      setSelectedColumnId(null);
                    }}
                    onSelectColumn={(cid) => {
                      setSelectedTableId(t.id);
                      setSelectedColumnId(cid);
                    }}
                    onHeaderPointerDown={(e) => onTableHeaderPointerDown(e, t.id)}
                    onBeginConnect={beginConnect}
                    onCompleteConnect={completeConnect}
                  />
                ))}
              </div>

              {/* toast */}
              {toast && (
                <div className="absolute left-4 bottom-4">
                  <div className="rounded-2xl border border-white/12 bg-black/60 backdrop-blur-xl px-4 py-2 text-sm text-white/85">
                    {toast}
                  </div>
                </div>
              )}
            </div>
          </Glass>
        </div>
      </div>
    </AppBackground>
  );
}

/* ----------------------------- Node UI ----------------------------- */

function TableNode({
  table,
  selected,
  selectedColumnId,
  onSelectTable,
  onSelectColumn,
  onHeaderPointerDown,
  onBeginConnect,
  onCompleteConnect,
}) {
  return (
    <div
      className={[
        "absolute  select-none rounded-3xl border backdrop-blur-2xl ",
        selected
          ? "border-white/18 bg-white/[0.06] ring-2 ring-white/35 shadow-[0_28px_90px_-35px_rgba(255,255,255,0.35)]"
          : "border-white/12 bg-white/[0.05] shadow-[0_18px_50px_-22px_rgba(0,0,0,0.78)]",
      ].join(" ")}
      style={{
        left: table.position.x,
        top: table.position.y,
        width: table.size?.w || 360,
        // height: table.size?.h || 280,
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        onSelectTable();
      }}
    >
      {/* premium accent */}
      <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-400/14 via-sky-400/10 to-transparent" />
      <div className="pointer-events-none absolute inset-0 rounded-3xl [mask-image:radial-gradient(450px_240px_at_35%_0%,black,transparent)] bg-white/10" />

      {/* header (drag grip only) */}
      <div
        className="relative px-4 pt-4 pb-3 border-b border-white/10 cursor-grab active:cursor-grabbing select-none"
        onPointerDown={(e) => {
          e.stopPropagation();
          onHeaderPointerDown(e);
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2">
            <div className="h-8 w-8 rounded-2xl border border-white/10 bg-white/6 flex items-center justify-center flex-shrink-0">
              <Table2 className="h-4 w-4 text-white/80" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{table.name}</div>
              <div className="text-[11px] text-white/45 -mt-0.5">Drag to move</div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Pill tone="emerald" className="hidden sm:inline-flex">
              <KeyRound className="h-3.5 w-3.5" />
              Schema
            </Pill>
            <div className="h-8 w-8 rounded-xl border border-white/10 bg-white/6 flex items-center justify-center text-white/50">
              <GripVertical className="h-4 w-4" />
            </div>
          </div>
        </div>
      </div>

      {/* columns */}
      <div className="relative px-4 pb-4 pt-3">
        <div className="rounded-2xl border border-white/10 bg-black/25 overflow-hidden">
          <div className="px-3 py-2 border-b border-white/10 text-[12px] text-white/65 flex items-center justify-between">
            <span className="inline-flex items-center gap-2">
              <Columns3 className="h-4 w-4 text-white/65" />
              Columns ({table.columns.length})
            </span>
            <span className="text-white/40">out → in</span>
          </div>

          <div className=" overflow-hidden h-full px-5">
            {table.columns.map((c) => {
              const active = selectedColumnId === c.id;
              return (
                <div
                  key={c.id}
                  className={[
                    "relative px-3 py-2.5 flex items-center gap-2 border-b border-white/5 last:border-b-0",
                    active ? "bg-white/10" : "hover:bg-white/6",
                    "transition",
                  ].join(" ")}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    onSelectColumn(c.id);
                  }}
                >
                  {/* IN handle (left) */}
                  <div
                    className="absolute left-[-12px] top-1/2 -translate-y-1/2"
                    data-table-id={table.id}
                    data-column-id={c.id}
                    onPointerUp={(e) => onCompleteConnect(e, table.id, c.id)}
                    title="Reference target (PK/UK)"
                  >
                    <HandleDot tone="in" tableId={table.id} columnId={c.id} />
                  </div>

                  {/* OUT handle (right) */}
                  <div
                    className="absolute right-[-12px] top-1/2 -translate-y-1/2"
                    onPointerDown={(e) => onBeginConnect(e, table.id, c.id)}
                    title="Foreign key from (child)"
                  >
                    <HandleDot tone="out" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {c.pk && (
                        <Pill tone="emerald">
                          <KeyRound className="h-3.5 w-3.5" /> PK
                        </Pill>
                      )}
                      {!c.pk && c.unique && <Pill tone="sky">UK</Pill>}
                      <div className="text-sm font-semibold truncate">{c.name}</div>
                    </div>
                    <div className="text-[12px] text-white/60 font-mono truncate">
                      {c.type}{" "}
                      <span className={c.nullable ? "text-white/45" : "text-white/70"}>
                        {c.nullable ? "NULL" : "NOT NULL"}
                      </span>
                    </div>
                  </div>

                  {active && (
                    <div className="text-[11px] text-white/45">
                      {c.pk ? "Primary key" : c.unique ? "Unique" : "Field"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-[11px] text-white/45">
          <span>Tip: release on the blue dot to create FK</span>
          <span className="font-mono">{String(table.id).slice(0, 6)}</span>
        </div>
      </div>
    </div>
  );
}

function HandleDot({ tone, tableId, columnId }) {
  const cls =
    tone === "out"
      ? "bg-emerald-300/95 shadow-[0_0_0_7px_rgba(16,185,129,0.13)]"
      : "bg-sky-300/95 shadow-[0_0_0_7px_rgba(56,189,248,0.13)]";

  return (
    <div
      data-table-id={tableId}
      data-column-id={columnId}
      className={[
        "h-6 w-6 rounded-full border border-white/25",
        "backdrop-blur-xl",
        "grid place-items-center",
        cls,
      ].join(" ")}
    >
      <div
        data-table-id={tableId}
        data-column-id={columnId}
        className="h-2 w-2 rounded-full bg-black/35"
      />
    </div>
  );
}

/* ----------------------------- Geometry ----------------------------- */

function getColumnAnchor(tables, tableId, columnId, side) {
  const t = tables.find((x) => x.id === tableId);
  if (!t) return null;
  const idx = t.columns.findIndex((c) => c.id === columnId);
  if (idx < 0) return null;

  // IMPORTANT: must match node layout above
  const x = t.position.x;
  const y = t.position.y;

  const headerH = 60;    // header section (pt-4 pb-3 + content ~60px)
  const colsHeaderH = 38; // columns header bar (px-3 py-2 + border ~38px)
  const rowH = 48;        // each row (py-2.5 top+bottom = 20px + content ~48px)
  const topOffset = headerH + colsHeaderH + 12; // 12px = pt-3 from column container padding-top
  const rowCenterY = y + topOffset + idx * rowH + rowH / 2;

  const w = t.size?.w || 360;

  if (side === "in") return { x: x - 12, y: rowCenterY };
  return { x: x + w + 12, y: rowCenterY };
}

function makeBezier(a, b) {
  const dx = Math.max(120, Math.abs(b.x - a.x) * 0.52);
  const c1 = { x: a.x + dx, y: a.y };
  const c2 = { x: b.x - dx, y: b.y };
  return `M ${a.x} ${a.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${b.x} ${b.y}`;
}
